/* eslint-disable */
import { useMemo, useState } from "react";
import { AlertCircle, Loader, Plus, X } from "lucide-react";
import {
  resolveDiscordInvites,
  importDiscordServers,
  formatCompactNumber,
  formatPercent,
} from "../pages/discordApi";
import "./discordResolverPanel.css";

const MAX_URLS = 25;

const STATUS_LABEL = {
  new: "NEW",
  already_tracked: "ALREADY TRACKED",
  invalid: "INVALID",
  error: "ERROR",
};

// server_id is a 64-bit Discord snowflake and is treated as a string everywhere
// in this file. Never Number()/parseInt() it.
function rowKey(result, index) {
  return result.server_id
    ? `id:${result.server_id}`
    : `url:${index}:${result.original_url}`;
}

export default function DiscordResolverPanel({ onImportSuccess }) {
  const [open, setOpen] = useState(false);
  const [urlsText, setUrlsText] = useState("");
  const [results, setResults] = useState([]);
  const [resolving, setResolving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState({});
  const [games, setGames] = useState({});

  const urls = useMemo(
    () => urlsText.split("\n").map((u) => u.trim()).filter(Boolean),
    [urlsText]
  );

  const clearResults = () => {
    setResults([]);
    setSelected({});
    setGames({});
    setError("");
  };

  const handleResolve = async () => {
    if (!urls.length) return;
    setResolving(true);
    setError("");
    try {
      const data = await resolveDiscordInvites(urls.slice(0, MAX_URLS));
      const rows = Array.isArray(data.results) ? data.results : [];
      setResults(rows);
      // New servers are import candidates by default.
      const preselected = {};
      rows.forEach((r, i) => {
        if (r.status === "new") preselected[rowKey(r, i)] = true;
      });
      setSelected(preselected);
      setGames({});
    } catch (err) {
      setError(String(err.message || err));
      setResults([]);
    } finally {
      setResolving(false);
    }
  };

  // Importable rows: selected new servers, plus already-tracked servers the admin
  // typed a game name for (those add a game link only, never a second server row).
  const payload = useMemo(() => {
    const out = [];
    results.forEach((r, i) => {
      const key = rowKey(r, i);
      const game = (games[key] || "").trim();
      if (r.status === "new" && selected[key]) out.push({ row: r, game });
      else if (r.status === "already_tracked" && game) out.push({ row: r, game });
    });
    return out;
  }, [results, selected, games]);

  const handleImport = async () => {
    if (!payload.length) return;
    setImporting(true);
    setError("");
    try {
      const data = await importDiscordServers(
        payload.map(({ row, game }) => ({
          server_id: String(row.server_id),
          server_name: row.server_name,
          invite_code: row.invite_code,
          original_url: row.original_url,
          canonical_url: row.canonical_url,
          source_type: row.source_type,
          members: row.members,
          online: row.online,
          boosts: row.boosts,
          description: row.description,
          vanity_url_code: row.vanity_url_code,
          features: row.features,
          icon_url: row.icon_url,
          splash_url: row.splash_url,
          banner_url: row.banner_url,
          verified: !!row.verified,
          partnered: !!row.partnered,
          associated_game: game || null,
        }))
      );

      const parts = [];
      if (data.servers_added)
        parts.push(`Added ${data.servers_added} server${data.servers_added === 1 ? "" : "s"}`);
      if (data.game_links_added)
        parts.push(`${data.game_links_added} game link${data.game_links_added === 1 ? "" : "s"}`);
      if (!parts.length) parts.push("Nothing new to add");

      if (data.errors && data.errors.length) {
        setError(data.errors.join(" · "));
      } else {
        setUrlsText("");
        clearResults();
        setOpen(false);
      }

      onImportSuccess?.(parts.join(" · "), data);
    } catch (err) {
      setError(String(err.message || err));
    } finally {
      setImporting(false);
    }
  };

  const newCount = results.filter((r) => r.status === "new").length;

  return (
    <div className="discord-resolver">
      <button
        type="button"
        className={`discord-resolver-toggle${open ? " is-open" : ""}`}
        onClick={() => setOpen((v) => !v)}
      >
        <Plus size={14} /> Add Servers
      </button>

      {open && (
        <div className="discord-resolver-panel">
          <div className="discord-resolver-head">
            <span>Add Discord Servers</span>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close">
              <X size={14} />
            </button>
          </div>

          <div className="discord-resolver-body">
            <label className="discord-resolver-label">Discord URLs</label>
            <textarea
              className="discord-resolver-textarea"
              rows={4}
              spellCheck={false}
              placeholder={
                "https://discord.gg/example\n" +
                "https://discord.com/invite/example\n" +
                "https://discord.com/servers/game-name-123456789012345678"
              }
              value={urlsText}
              onChange={(e) => setUrlsText(e.target.value)}
              disabled={resolving}
            />

            <div className="discord-resolver-actions">
              <span className="discord-resolver-count">
                {urls.length ? `${urls.length} URL${urls.length === 1 ? "" : "s"}` : ""}
                {urls.length > MAX_URLS ? ` · only first ${MAX_URLS} used` : ""}
              </span>
              <button
                type="button"
                className="discord-resolver-primary"
                onClick={handleResolve}
                disabled={resolving || !urls.length}
              >
                {resolving ? <Loader size={13} className="dr-spin" /> : null}
                {resolving ? "Resolving…" : "Resolve"}
              </button>
            </div>

            {error && (
              <div className="discord-resolver-error">
                <AlertCircle size={13} /> {error}
              </div>
            )}

            {results.length > 0 && (
              <>
                <div className="discord-resolver-divider">
                  <span>
                    {results.length} result{results.length === 1 ? "" : "s"}
                    {newCount ? ` · ${newCount} new` : ""}
                  </span>
                  <button type="button" className="discord-resolver-link" onClick={clearResults}>
                    Clear
                  </button>
                </div>

                <div className="discord-resolver-list">
                  {results.map((r, i) => {
                    const key = rowKey(r, i);
                    const failed = r.status === "invalid" || r.status === "error";
                    const art = r.splash_url || r.banner_url || r.icon_url;
                    return (
                      <div key={key} className={`discord-resolver-row is-${r.status}`}>
                        <div className="discord-resolver-row-top">
                          {r.status === "new" ? (
                            <input
                              type="checkbox"
                              checked={!!selected[key]}
                              onChange={(e) =>
                                setSelected((s) => ({ ...s, [key]: e.target.checked }))
                              }
                            />
                          ) : (
                            <span className="discord-resolver-spacer" />
                          )}

                          {art && !failed ? (
                            <img className="discord-resolver-art" src={art} alt="" />
                          ) : (
                            <span className="discord-resolver-art is-empty" />
                          )}

                          <div className="discord-resolver-ident">
                            <div className="discord-resolver-name">
                              {r.server_name || r.original_url}
                            </div>
                            {r.server_id && (
                              <div className="discord-resolver-id">{r.server_id}</div>
                            )}
                          </div>

                          <span className={`discord-resolver-pill is-${r.status}`}>
                            {STATUS_LABEL[r.status] || String(r.status).toUpperCase()}
                          </span>
                        </div>

                        {failed ? (
                          <div className="discord-resolver-row-error">
                            {r.error || "Could not resolve"}
                            <span className="discord-resolver-src">{r.original_url}</span>
                          </div>
                        ) : (
                          <>
                            <div className="discord-resolver-stats">
                              <span>
                                <b>{formatCompactNumber(r.members) ?? "—"}</b> Members
                              </span>
                              <span>
                                <b>{formatCompactNumber(r.online) ?? "—"}</b> Online
                              </span>
                              <span>
                                <b>{formatPercent(r.online_pct) ?? "—"}</b> Active
                              </span>
                              <span>
                                <b>{formatCompactNumber(r.boosts) ?? "—"}</b> Boosts
                              </span>
                              {r.verified && <em className="discord-resolver-tag">Verified</em>}
                              {r.partnered && <em className="discord-resolver-tag">Partnered</em>}
                            </div>

                            {r.duplicate && (
                              <div className="discord-resolver-note">
                                {(r.submitted_urls?.length || 2)} pasted URLs resolve to this same server
                              </div>
                            )}

                            {r.status === "already_tracked" && (
                              <div className="discord-resolver-note is-tracked">
                                Already in the catalog — no new server row will be created.
                                {r.existing_game_links?.length
                                  ? ` Linked games: ${r.existing_game_links.join(", ")}`
                                  : ""}
                              </div>
                            )}

                            <input
                              className="discord-resolver-game"
                              type="text"
                              placeholder={
                                r.status === "already_tracked"
                                  ? "Add game association (optional)"
                                  : "Associated game (optional)"
                              }
                              value={games[key] || ""}
                              onChange={(e) =>
                                setGames((g) => ({ ...g, [key]: e.target.value }))
                              }
                            />
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>

                <div className="discord-resolver-actions">
                  <span className="discord-resolver-count">
                    {payload.length
                      ? `${payload.length} to apply`
                      : "Select a new server or add a game name"}
                  </span>
                  <button
                    type="button"
                    className="discord-resolver-primary"
                    onClick={handleImport}
                    disabled={importing || !payload.length}
                  >
                    {importing ? <Loader size={13} className="dr-spin" /> : null}
                    {importing ? "Adding…" : "Add Selected"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
